/**
 * Admin-panel session, shared vocabulary, and the last of the legacy fetch layer.
 * Talks to the backend at VITE_API_BASE (dev → :8444, prod → :443).
 * The session token is kept in localStorage and sent as a Bearer header.
 *
 * WHAT LIVES HERE NOW that the panel is on `/v1` (`@api/v1/`, doc 04):
 *
 *   * The SESSION. `getToken`/`setToken`/`clearToken`, `ApiError`, `handle401` — the
 *     one 401/402 policy for the whole app. `@api/v1/envelope` builds on these on
 *     purpose: `CodedApiError extends ApiError`, so every `if (handle401(err, navigate))`
 *     keeps its exact behaviour whichever surface answered.
 *   * The three routes with NO v1 twin by design (doc 04 §4.8): `POST /admin/login`,
 *     `POST /admin/register` and the unauthenticated `GET /admin/invites/preview`.
 *     v1 has no auth surface.
 *   * `parseTs` and the day/instant resolvers — the timezone rules both surfaces obey.
 *   * The SHARED TYPES the components are written against. A v1 module imports these
 *     and projects its response onto them (the moderation and winback
 *     mappers), which is why the migration changed almost no component.
 *   * Three legacy analytics reads with live callers: `fetchMemberAnalytics`,
 *     `fetchTopContent`, `fetchAiInsights`. Everything else has moved.
 *
 * Do not add a client here — one file per resource under `@api/v1/`.
 */

import { identifyUser, resetAnalytics } from './analytics';

const API_BASE = import.meta.env['VITE_API_BASE'] ?? 'http://localhost:8000';

/**
 * What a member IS, and how a date reads — defined once in `@ui/member` now, because
 * the extension renders the same things and was keeping a hand-maintained copy.
 * Re-exported here so the call sites that already say `from '../admin'` keep working.
 */
export {
  parseTs, timeAgo, timeUntil, fmtPrice, planParts, memberStatusMeta,
  MEMBER_STATUS_META,
  type PlanInterval, type MemberPlan, type MemberRenewal, type MemberStatus,
  type MemberStatusMeta, type HeatmapDay, type RoleJourneySegment, type MemberCard,
  type ImageMedia, type VideoMedia,
} from '@ui/member';
export { type MemberStage } from '@ui/stages';

// The member/media types this module used to describe its OWN shapes with are no
// longer needed in local scope: those shapes moved to `@api/v1/shapes`. The
// re-exports above still hand every name to the pages that import them from here.
import type { MemberStatus } from '@ui/member';
import type { OnboardingStage } from '@api';
// Used by this module's own helpers, not just re-exported.
import { parseTs } from '@ui/member';
const TOKEN_KEY = 'sg_admin_token';

export interface AdminUser {
  email: string;
  name: string;
  role: 'creator' | 'superadmin';
  /** Every community this creator administers. Always sent — `/v1/me`'s `MeUser`
   *  declares it required — so no caller has to handle its absence. */
  skool_ids: number[];
  /** Set for a self-registered creator whose community isn't ingested yet. */
  pending_skool_url?: string | null;
  /** ACCOUNT-level derived plan state; absent on an older API. 'legacy' =
   *  grandfathered/superadmin/every self-serve signup (never gated). Billing itself is
   *  per-community — TrialBanner/AdminShell follow the active community's plan_status
   *  (see Community below); this field only routes a lapsed stub-era account. */
  plan_status?: 'legacy' | 'trial' | 'expired' | 'paid' | 'free_for_life';
  /** ISO end-of-trial; null for legacy/paid. Only set for stub-era accounts. */
  trial_ends_at?: string | null;
  /** Where this account's signup stands. Absent on an older API. */
  onboarding_stage?: OnboardingStage;
}

/** How many communities this account administers. */
export function communityCount(me: AdminUser): number {
  return me.skool_ids.length;
}

/** Where signup continues, and where every account that holds no community lands. */
export const DISCOVERY_PATH = '/admin/insights/discovery';

/**
 * Global admin gatekeeper, called right after the bootstrap on every admin page. Routes,
 * in order: a stub-era account whose user-level trial lapsed → /admin/expired; a creator
 * who holds no community yet → Discovery, where signup carries on while the community is
 * joined and indexed. Returns true if it redirected; the caller should then stop
 * initialising.
 *
 * Per-community trial expiry is NOT handled here: AdminShell gates the panel body when
 * the *active* community is expired, and a server-side 402 — fired only when EVERY
 * community is expired — still bounces to the full-page paywall via handle401.
 */
export function redirectIfAwaitingOnboarding(
  me: AdminUser,
  navigate: (to: string) => void,
): boolean {
  if (me.role !== 'creator') return false;
  if (me.plan_status === 'expired') {
    navigate('/admin/expired');
    return true;
  }
  if (communityCount(me) === 0) {
    navigate(DISCOVERY_PATH);
    return true;
  }
  return false;
}

// `AdminMessage` / `AdminConversation` / `AdminUserGroup` / `ConversationsResponse` and
// `fetchConversations` lived here. The Conversations page reads `GET …/ai/askers` and
// `GET …/ai/conversations[/{conversation_id}]` (`@api/v1/ai`): a paged asker
// list plus a transcript fetched on expand, instead of one payload that hydrated 500
// conversations with every message to render ten (doc 04 §5.5).

// `ApiError` moved to `@api/errors` — every caller of every surface throws it, so
// it belongs with the client rather than in this app's module. Re-exported here.
export { ApiError } from '@api/errors';
import { ApiError } from '@api/errors';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  resetAnalytics(); // drop the PostHog person so the next session isn't misattributed
}


/**
 * How long something took, for the health grid: "8ms", "1.2s", "47s", "3m 12s", "1h 4m".
 * Returns "—" for null.
 *
 * Sub-second keeps its milliseconds because that is the interesting range: a step that
 * declined its delta reports single-digit ms, and telling that apart from one that
 * actually did work is the whole point of showing this. Above a second the precision
 * stops mattering, so it degrades to one decimal and then to whole units.
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = ms / 1000;
  if (s < 10) return `${s.toFixed(1)}s`;
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${Math.round(s % 60)}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** The same, for a pair of timestamps — job rows carry `started_at`/`finished_at`
 *  rather than a precomputed duration. Null while still running. */
export function durationBetween(startIso: string | null, endIso: string | null): number | null {
  if (!startIso || !endIso) return null;
  const a = parseTs(startIso).getTime();
  const b = parseTs(endIso).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.max(0, b - a);
}


/**
 * Shared 401 handler: on an expired/invalid session, clear the token and bounce
 * to login with a notice. Returns true if it handled a 401 (caller should stop).
 * Also catches 402 "trial_expired" (a mid-session trial expiry on a data route)
 * and bounces to the paywall screen instead — the session itself is still valid.
 * Pass the router's `navigate` to avoid a circular import.
 */
export function handle401(err: unknown, navigate: (to: string) => void): boolean {
  if (err instanceof ApiError && err.status === 402) {
    navigate('/admin/expired');
    return true;
  }
  if (err instanceof ApiError && err.status === 401) {
    clearToken();
    try {
      sessionStorage.setItem('sg_admin_notice', 'Your session expired — please sign in again.');
    } catch {
      /* ignore */
    }
    navigate('/admin/login');
    return true;
  }
  return false;
}

async function detail(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { detail?: string };
    return body.detail ?? `${res.status} ${res.statusText}`;
  } catch {
    return `${res.status} ${res.statusText}`;
  }
}

/** Abort stuck requests instead of spinning forever. A request that outlives
 * this deadline is a dead keep-alive socket (laptop woke from sleep) or a
 * stalled server — either way the page must get an error it can show, because
 * a promise that never settles leaves the loading skeleton up for good.
 * The LLM suggest stream is exempt: it has visible progress and its own signal. */
const REQUEST_TIMEOUT_MS = 20_000;

async function fetchT(input: string, init: RequestInit = {}): Promise<Response> {
  try {
    return await fetch(input, { ...init, signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'TimeoutError') {
      throw new ApiError(0, 'The server didn’t respond — check your connection and retry.');
    }
    throw err;
  }
}

export async function login(email: string, password: string): Promise<AdminUser> {
  const res = await fetchT(`${API_BASE}/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw new ApiError(res.status, await detail(res));
  const body = (await res.json()) as { access_token: string; user: AdminUser };
  setToken(body.access_token);
  identifyUser(body.user); // tie the session + recording to this person in PostHog
  return body.user;
}

/**
 * Self-serve owner registration: creates a `creator` account and records the
 * Skool community URL they want Sideguide to manage. Returns the same session
 * shape as login (token + user) and stores the token, so the caller lands
 * signed in. Onboarding (actually ingesting the community) happens later.
 * With `inviteToken` the invite's community is authoritative server-side
 * (the skoolUrl field is ignored); a used/expired token comes back as 410.
 * `via` is the stored affiliate referral code (see lib/affiliates.storedVia) —
 * passed through only when a fresh code exists; the server ignores a bad/unknown
 * code, so a stale referral never blocks a signup.
 */
/**
 * Create the account. Step 1 of signup is an EMAIL and nothing else — the password
 * is chosen at the end of the wizard (`setOnboardingPassword`), once we've been
 * added to the community. The server still accepts a password for a caller that
 * wants a complete account in one call; the signup form no longer sends one.
 *
 * A 409 means the address is taken by an account that has really been set up. An
 * address left behind by an abandoned signup resumes instead, returning a session
 * token exactly as a fresh registration does.
 */
/** What `startSignup` answers: the community to show, and the token `register` redeems. */
export interface SignupStarted {
  signup: string;
  community: { slug: string; display_name: string; logo_url: string; total_members: number };
}

/**
 * Signup's first step: the community link. The server resolves it, refuses one another
 * account already has, and starts its keyword audit — so the report is running while the
 * visitor chooses a password. Throws `ApiError` with a readable message on 404 (no
 * community there), 409 (already claimed), 422 (not a Skool link) and 429.
 */
export async function startSignup(url: string): Promise<SignupStarted> {
  const res = await fetchT(`${API_BASE}/admin/signup/community`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  });
  if (!res.ok) throw new ApiError(res.status, await detail(res));
  return (await res.json()) as SignupStarted;
}

/** Signup's second step: the account, claiming the community `startSignup` named (or the
 *  one an invite link names). Stores the session, so the caller lands signed in. */
export async function register(input: {
  email: string;
  password: string;
  signup?: string;
  inviteToken?: string;
  via?: string;
}): Promise<AdminUser> {
  const res = await fetchT(`${API_BASE}/admin/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: input.email,
      password: input.password,
      ...(input.signup ? { signup: input.signup } : {}),
      ...(input.inviteToken ? { invite: input.inviteToken } : {}),
      ...(input.via ? { via: input.via } : {}),
    }),
  });
  if (!res.ok) throw new ApiError(res.status, await detail(res));
  const body = (await res.json()) as { access_token: string; user: AdminUser };
  setToken(body.access_token);
  identifyUser(body.user); // tie the session + recording to this person in PostHog
  return body.user;
}

/**
 * Ask for a password-reset email. Always resolves — the server answers 200 whether or
 * not the address has an account (never an oracle), so the page shows the same "check
 * your inbox" either way.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const res = await fetchT(`${API_BASE}/admin/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new ApiError(res.status, await detail(res));
}

/**
 * Spend a reset link: set a new password and land signed in. Returns the same session
 * shape as `login` and stores the token, so the reset page can navigate straight to the
 * dashboard. A 400 means the link is invalid, expired, or already used.
 */
export async function resetPassword(token: string, password: string): Promise<AdminUser> {
  const res = await fetchT(`${API_BASE}/admin/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  if (!res.ok) throw new ApiError(res.status, await detail(res));
  const body = (await res.json()) as { access_token: string; user: AdminUser };
  setToken(body.access_token);
  identifyUser(body.user);
  return body.user;
}

// `fetchMe()` is gone. `GET /admin/me` was the first leg of the 3-deep preamble
// every page opened with; the account, its communities and the server-resolved
// selection now arrive together from `GET /v1/me` (`@api/v1/me`, doc 04 §4.1).
// `identifyUser()` moved with it — `asAdminUser()` is where a bootstrap becomes the
// `AdminUser` the pages still type against.

// --- onboarding wizard -------------------------------------------------------
//
// `authedSend()` is gone with the last legacy write. Every remaining wrapper in this
// file is a GET on `authed()`; v1 writes go through `@api/v1/http`/
// `apiDelete` (`@api/v1/envelope`), which speak the one `{error:{code,…}}` shape.

/** Derived per-community plan state (never stored server-side). 'legacy' (a
 *  grandfathered skool), 'free' (under the member limit) and 'free_for_life' (a
 *  hand-granted comp) are never gated.
 *
 *  'paid' means MONEY IS ARRIVING — a live Stripe subscription — and nothing else.
 *  'free_for_life' is the giveaway that used to share the 'paid' token; keeping them
 *  apart is what stops the affiliate page reporting comps as conversions. */
export type CommunityPlanStatus =
  | 'paid'
  | 'free_for_life'
  | 'free'
  | 'trial'
  | 'expired'
  | 'legacy'
  | 'needs_payment';

export interface Community {
  id: number;
  slug: string;
  name: string;
  /** The community's Skool logo (hotlinkable URL); null when we have none — the UI
   *  falls back to an initial-letter tile. */
  logo_url?: string | null;
  total_courses: number | null;
  total_members: number | null;
  /** This community's plan tier; absent on an API that predates per-skool billing
   *  (treat a missing value as 'legacy' — ungated). */
  plan_status?: CommunityPlanStatus;
  /** ISO end-of-trial for THIS community; set only while plan_status === 'trial'. */
  trial_ends_at?: string | null;
  /**
   * Whether we can read this community's members page — the gate on every membership,
   * revenue and churn number.
   *
   *  'ok'       — a pass read the four membership tabs; nothing to ask for.
   *  'ask'      — we hold no staff role here, so we cannot read the members page. Known
   *               from our own role (the `roster` stage syncs it everywhere), not from
   *               probing — the sweep is gated on that role and never runs here.
   *  'escalate' — we hold a staff role and Skool STILL withholds the tabs, so the role
   *               we were given isn't the one that unlocks billing.
   *  'unknown'  — we can't say: staff but the first sweep is pending, or our own Skool
   *               id is unresolvable. Render NOTHING.
   *
   * Absent on an API that predates this; treat that as 'unknown' for the same reason.
   */
  roster_state?: RosterState;
  /** The role our scraper account currently holds here, so the prompt can acknowledge
   *  what the owner has already granted instead of repeating the ask. */
  roster_role?: 'owner' | 'admin' | 'moderator' | null;
}

export type RosterState = 'ok' | 'ask' | 'escalate' | 'unknown';

// `RosterAccount` / `fetchRosterAccount()` are gone. The Skool account Sideguide
// reads communities as is `GET /v1/scraper-account` (`fetchScraperAccount`,
// `@api/v1/me`) — still one global fact, fetched once by the shell.

// The Skool classroom (Course → Section → Page → Resource) moved to `/v1`:
// `GET|PATCH /v1/communities/{skool_id}/classroom` (`@api/v1/admin`). `toggle` and
// `toggle-all` became ONE declarative PATCH that answers with the new totals, so the
// Indexed Content page no longer re-reads the whole tree to flip a boolean (doc 02 W8).

// `fetchCommunities()` is gone. `GET /admin/communities` was the second leg of the
// 3-deep preamble and the trial page's 5-second poll; the community list now
// arrives with the account in `GET /v1/me` (`@api/v1/me`), which `community.svelte.ts`
// is a view over. The `Community` type stays — it is what that state holds.

// --- billing (per-community Stripe) -----------------------------------------
//
// All three wrappers moved to `/v1` (`@api/v1/admin`): `startCheckout()` →
// `startCheckout` (`POST /v1/communities/{id}/billing/checkout`),
// `openBillingPortal()` → `openBillingPortal` and `syncBilling()` → `syncBilling`
// (`POST /v1/me/billing/…`). `sync` answers with the whole `/v1/me` body, which is
// what collapses the five-round-trip return from Stripe Checkout (doc 02 W10).
// TrialBanner and ExpiredPaywall are the checkout callers.

// --- community posts & comments ---------------------------------------------


// `PostListItem` / `PostsResponse` and `fetchPosts` were here. The feed list moved to
// `GET /v1/communities/{id}/posts` (`@api/v1/posts`), whose row is `PostItem`
// and whose envelope is the one `{items, next_cursor, total}` every v1 list uses. The
// two pages that read them — Timeline and the member profile — are the only callers
// there were. `fetchPost()` went the same way, to `fetchPost` (`GET …/posts/{id}`);
// `GET /admin/posts/{post_id}` stays MOUNTED because it is FROZEN for the extension
// (doc 04 §3.3 row 54), it simply has no web-app caller any more. `PostDetail` and
// `CommentItem` below stay: `fetchPostThread()` in `@api/v1/posts` projects the v1
// resource onto them, so every thread component renders unchanged.


/** Ordering for the posts feed. Backend maps these to columns. */
export type PostSort = 'new' | 'comments' | 'upvotes';

/** Date-range presets for the timeline filter (rolling windows from now + custom). */
export type RangePreset = 'all' | '3d' | '7d' | '30d' | '90d' | 'custom';

// --- members explorer -------------------------------------------------------


/** Chip order: Skool's tab order — current states first, then the ways out. */
export const MEMBER_STATUS_ORDER: MemberStatus[] = [
  'active', 'cancelling', 'churned', 'left', 'removed', 'banned',
];


// `MemberRow` / `MemberSort` / `MembersResponse` and `fetchMembers` were here, and
// `fetchPost` with them. The members explorer reads `GET /v1/communities/{id}/members`
// (`@api/v1/members`): the same filters as query params, but the row is assembled
// from named blocks — `membership`, `stats`, `journey`, `activity` — so a table that
// renders no heatmap does not ship 365 days per row (doc 04 §5.1, −88% payload).

// `MemberCommentItem` / `MemberCommentsResponse` and `fetchMemberComments` were
// here. A member's comment feed moved to `GET /v1/communities/{id}/comments`
// (`@api/v1/posts`), whose row already carries `post_id` and takes the parent
// block on `?include=post` rather than shipping it to every caller. The member
// profile page was the only caller.

// --- analytics --------------------------------------------------------------

// What the server may send in a `stage` field. Wider than `Stage` in @ui/stages
// (which owns the display taxonomy) by exactly one value: `departed`, the loader's
// terminal storage marker, read off the roster sweep rather than from activity. It
// is never rendered on the momentum axis — pass any stage through `stageMeta()`,
// which returns null for it, instead of indexing STAGE_META directly.


// The Engagement page "Top content" group — posts + comments, period-controlled.

// `CommunityAnalytics` / `fetchCommunityAnalytics` lived here. The Overview reads
// `GET /v1/communities/{id}/insights` now — one request for the blocks it renders
// instead of three payloads carrying four blocks it never rendered at all and two
// copies of the same clock grid (docs/api-v1/04-v1-surface.md §4.3, §5.2). The
// shapes are in `@api/v1/insights`.


// One reply-network partner: how many replies this member sent them vs got back.

// `fetchMemberAnalytics` (`GET /admin/analytics/member`) is GONE. Its replacement is
// `fetchMemberAnalytics` in `@api/v1/members`, which asks the member resource for
// `?include=analysis,depth` — the two aggregations whose absence from `domain/` was the
// only reason this route outlived the rest of /admin/analytics.

// `MemberCard` / `fetchMemberCard` and `fetchMemberRoles` were here — the hovercard
// and the role-ring map. Both are the members resource on `/v1`: `fetchMemberCard`
// (`GET …/members/{id}` with the hovercard's blocks) and `fetchRoles`
// (`GET …/roles`, a `RoleMap` beside the community block rather than a bare body).
// Their legacy routes stay MOUNTED — `member-card` and `member-roles` are frozen for
// the extension (doc 04 §3.3 rows 15–16) — they just have no web-app caller now.

// --- insights sub-pages -------------------------------------------------------


// The win-back Queue + board (`ReactivationRow`, `ReactivationPlan`, `PlanStatus`, …)
// were retired with those surfaces — the CRM Pipelines Engagement board replaces them
// (`routes/AdminPipelines.svelte`, `@api/v1/pipelines`). `ReactivationPlay`, the LLM
// "play" shape, outlived them on the pipeline card until the generator behind it went
// too: `POST …/cards/{id}/plan` had no caller left once the board was deleted, so the
// endpoint, `crm_cards.plays` and the shape are all gone.

// `ScatterMember` / `ContributorInsights` lived here. The Contributors page reads
// `…/insights?include=census,census_spark,scatter,cohorts,role_volume`; the Overview
// asks for the first two and the scatter is never built for it (§5.2). Shapes in
// `@api/v1/insights`.


// `ContentInsights` lived here. The Engagement page reads
// `…/insights?include=labels,timing,clock` — three named blocks on the one insights
// route rather than a payload that recomputed the Overview's clock. Shapes in
// `@api/v1/insights`.

export interface LabelRow {
  label_id: string;
  name: string | null;
  source: string | null;
  posts: number;
}



// `RoleFlow` / `fetchRoleFlow` lived here, with `lib/charts/RoleFlow.svelte`. The
// alluvial was disabled behind a hard-coded `false` at
// `AdminInsightsContributors.svelte:27` while its endpoint — which "walks every
// member × month" — still fired on every community change and rendered nowhere
// (docs/api-v1/02 §4.7, 04 §3.3 row 19, §5.6). Route dropped, fetch and widget with
// it; if it is revived it comes back as an `insights` block.

// The win-back calls that lived here — `fetchReactivationInsights`,
// `fetchReactivationPlans`, `generateReactivationPlan`, `updateReactivationPlanStatus`
// — are GONE with the win-back Queue + board they served. The CRM Pipelines
// Engagement board replaces that surface; its board, card moves and plan generation
// are `@api/v1/pipelines` (`GET/POST …/pipelines/{key}/…`).

// `fetchTopContent` (`GET /admin/analytics/top-content`) is GONE — `fetchTopContent`
// in `@api/v1/insights` asks the insights route for `?include=top_posts,top_comments`.
// The `TopContent`/`PostBrief`/`CommentBrief` types above stay: they are the shape that
// adapter returns and `<TopContent>` renders.

// --- Sideguide AI usage insights ---------------------------------------------
// `AiTranscriptMessage` / `AiConversation` and `fetchAiConversation` were here. A
// transcript is `GET …/ai/conversations/{conversation_id}` (`fetchConversation`,
// `@api/v1/ai`) — a real sub-resource of the conversation list rather than a
// second top-level route taking the id as a query param.
// `fetchAiInsights` (`GET /admin/analytics/ai`) is GONE. That route was two responses
// welded together; v1 splits them along the seam and `fetchAiInsights`
// (`@api/v1/ai`) issues both halves in parallel — the `ai_*` insights blocks
// and the `ai/conversations` ledger. The types above stay: they are what it returns.

// `fetchLabels` and `setLabelName` were here — the timeline's category vocabulary and
// its rename. Both are the labels resource on `/v1` (`@api/v1/posts`):
// `GET /v1/communities/{id}/labels`, whose row is the same four fields `LabelRow`
// declares, and `PATCH …/labels/{label_id}` (`renameLabel`), which answers with the
// renamed label instead of `{status:'ok'}`. `LabelRow` itself stays: `TimelineFilters`
// takes it as a prop.

// --- community moderators ---------------------------------------------------

export type MemberRoleValue = 'owner' | 'admin' | 'moderator' | 'member';

export interface Moderator {
  member_id: number;
  external_id: string;
  handle: string;
  name: string;
  picture_profile: string | null;
  location: string | null;
  role: string | null;
}

// `fetchModerators()` and `setRole()` moved to `/v1`. Both are the members resource
// there: the roster is `GET …/members?staff=only&fields=role` and the write is
// `PATCH …/members/{member_id}` returning the updated member, so Preferences patches
// its team from the response instead of re-reading it (`@api/v1/admin`, doc 02 W9).
// `MemberRoleValue` and `Moderator` above stay — the first is the role vocabulary the
// page still spells, the second types `ModerationItem.author`.

// --- moderation inbox -------------------------------------------------------


// `ModerationInbox` was the envelope those two rode in, and `fetchModerationInbox`,
// `clearModerationItem`, `clearAllModeration` and `saveModerationReply` the four calls
// that read and wrote it. The inbox is `GET /v1/communities/{id}/moderation/inbox`
// (`@api/v1/moderation`) in the one list envelope, with `stats` beside `items`; a
// clear is `PATCH …/moderation/items/{kind}/{item_id}`, a bulk clear
// `POST …/moderation/items/actions/clear`, and a queued reply `POST …/outbox/items`
// (`queueReply`, `@api/v1/outbox`), which answers with the outbox item it created.
// `ModerationItem`/`ModerationStats` above stay: the v1 module projects onto them, so
// AdminModeration and the Helper-shaped components render unchanged. All four legacy
// routes stay MOUNTED — they are frozen for the extension (doc 04 §3.3 rows 40–42, 46).

// --- days and instants -------------------------------------------------------
//
// THE RULE, and the reason these four exist: **a bare `YYYY-MM-DD` is ambiguous
// and must never travel between two pieces of code that might disagree about what
// a day is.** The API is only ever sent INSTANTS. A day-string is converted to an
// instant at the point where its meaning is known, and that meaning is one of two
// things, never assumed:
//
//   * the viewer picked it in a date input  -> it is THEIR LOCAL day  -> localDay*
//   * the server computed it (heatmap buckets are `to_char(external_created_at,
//     'YYYY-MM-DD')` over a UTC column, so they are UTC days) -> utcDay*
//
// Getting this wrong is silent: the list simply shows a window shifted by the
// viewer's UTC offset from the one they clicked. It has bitten this codebase
// twice — once when the backend forced `YYYY-MM-DD` to UTC days (making "today"
// wrong by the offset in Berlin), and again when a heatmap cell's UTC day label
// was deep-linked into the timeline and converted as if it were local.


/** UTC ISO instant of the END of a LOCAL day — i.e. the start of the NEXT local
 *  day, the exclusive upper bound for an inclusive local-day range.
 *  Idempotent on instants, as `localDayStartISO`. */
export function localDayEndISO(day: string): string {
  if (day.includes('T')) return day;
  const d = new Date(day + 'T00:00:00');
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  return d.toISOString();
}

/** UTC ISO instant of the START of a UTC day. For day-strings the SERVER produced
 *  — heatmap buckets, cohort months — which are UTC and must not be run through
 *  the local-day helpers. */
export function utcDayStartISO(day: string): string {
  if (day.includes('T')) return day;
  return new Date(day + 'T00:00:00.000Z').toISOString();
}

/** UTC ISO instant of the END of a UTC day (exclusive upper bound). */
export function utcDayEndISO(day: string): string {
  if (day.includes('T')) return day;
  const d = new Date(day + 'T00:00:00.000Z');
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString();
}

// --- and back again, for DISPLAY only ----------------------------------------
//
// The four resolvers above are one-way on purpose: once a bound is an instant it
// is unambiguous, which is exactly why the URL and the API only ever carry
// instants. But a `<input type="date">` and a range chip need a `YYYY-MM-DD`
// back, and an input silently renders EMPTY when handed anything else — which is
// how a heatmap deep-link ended up showing two blank date fields and a chip
// reading `2026-07-27T00:00:00.000Z → 2026-07-28T00:00:00.000Z`.
//
// Going back has to guess which resolver produced the instant. The rule: **an
// instant that lands on UTC midnight is a server-produced UTC day; anything else
// is a local-day boundary.** That is safe rather than lucky — the only viewers
// for whom a LOCAL midnight is also a UTC midnight sit at UTC+0, and there the
// two readings name the same date anyway.
//
// Display only. Never feed the result of these back to the API: re-resolving a
// displayed day would re-apply an offset that is already baked into the instant.

function isUtcMidnight(d: Date): boolean {
  return (
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0
  );
}

/** `YYYY-MM-DD` in the VIEWER's zone. */
function localDayString(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** The day a range START instant should DISPLAY as. Passes a bare day-string
 *  through, so a value that never became an instant still renders. */
export function displayDayStart(iso: string | null): string | null {
  if (!iso) return null;
  if (!iso.includes('T')) return iso;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return isUtcMidnight(d) ? iso.slice(0, 10) : localDayString(d);
}

/** The day a range END instant should DISPLAY as. The stored bound is EXCLUSIVE
 *  (the next midnight), while the day a human means is the one before it — so
 *  step back a millisecond before reading the date off. */
export function displayDayEnd(iso: string | null): string | null {
  if (!iso) return null;
  if (!iso.includes('T')) return iso;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const inclusive = new Date(d.getTime() - 1);
  return isUtcMidnight(d) ? inclusive.toISOString().slice(0, 10) : localDayString(inclusive);
}


// --- outbox / reply queue ----------------------------------------------------
//
// `OutboxStatus` / `OutboxTarget` / `OutboxItem` / `OutboxHelper` / `OutboxList` and
// `fetchOutboxItems` / `discardOutboxItem` were here. The queue is the outbox resource
// on `/v1` (`@api/v1/outbox`): `GET /v1/communities/{id}/outbox/items` in the one
// list envelope, with `counts` and the Helper presence block BESIDE `items`, and
// `DELETE …/outbox/items/{item_id}` for a cancel — a discard is the removal of a
// queued item, not a POST to a verb. `GET /outbox/items` stays MOUNTED: it is frozen
// for the Helper (doc 04 §3.3 row 70).

// --- suggested replies + moderator voices -----------------------------------

/** A staff member with a generated voice profile, for the reply composer's picker. */
export interface VoiceOption {
  member_id: number;
  handle: string;
  name: string;
  role: string;
  picture_profile: string | null;
  /** The owner's voice — the default selection when nothing is picked. */
  is_default: boolean;
}

/** One source the suggested reply drew on — mirrors the `done` frame's source
 *  dicts. `url` is the canonical Skool module link (null when unavailable). */
export interface SourceRef {
  title: string | null;
  course_title: string | null;
  url: string | null;
}

// `fetchVoices` and `streamSuggestedReply` were here. Both are on `/v1`
// (`@api/v1/moderation`): `GET /v1/communities/{id}/voices` serves BOTH readings —
// the composer's picker (`fetchVoices`, profiled staff only) and the AI-Voices
// settings page (`?include=profile`, `@api/v1/admin`) — and a suggestion is
// `POST …/moderation/suggestions` (`streamSuggestedReply`), the same SSE frames over
// `v1PostStream`. `VoiceOption` and `SourceRef` above stay: the v1 module and the
// composer are typed against them. The two legacy routes stay MOUNTED — both are
// frozen for the extension (doc 04 §3.3 rows 47-48).

// --- trial + invites (superadmin) --------------------------------------------

/** Public preview of an invite link: what community it's bound to, if still valid. */
export interface InvitePreview {
  valid: boolean;
  skool_url?: string;
  community_name?: string;
  note?: string | null;
  reason?: string;
}

// `InviteRow` / `AccountRow` and the five superadmin calls that used them
// (`createInvite`, `fetchInvites`, `fetchAccounts`, `setAccountPaid`,
// `setAccountCommunities`) moved to `/v1`: `GET|POST /v1/invites`, `GET /v1/accounts`
// with server-side `?q=`, and ONE `PATCH /v1/accounts/{user_id}` for both writes, each
// answering with the row it changed (`@api/v1/admin`, doc 02 W9).
// `GET /admin/invites/preview` below STAYS unversioned — it is the one unauthenticated
// entry point in that group and its shape was already fine (doc 04 §4.8).

/** Public (no auth) — the token itself is the secret; nothing sensitive returned. */
export async function fetchInvitePreview(token: string): Promise<InvitePreview> {
  const res = await fetchT(`${API_BASE}/admin/invites/preview?token=${encodeURIComponent(token)}`);
  if (!res.ok) throw new ApiError(res.status, await detail(res));
  return res.json() as Promise<InvitePreview>;
}

// --- the account-invite magic link ------------------------------------------
//
// Both halves are unauthenticated and therefore live HERE rather than in `@api/v1/`:
// the token in the link is the credential, and claiming mints the session — the same
// reason `/admin/login`, `/admin/register` and the preview above stayed unversioned.
// The superadmin half (issue / list / revoke) IS on v1, in `@api/v1/admin`.

/** What a link will do, read WITHOUT spending it. Never throws for a dead token —
 *  an invalid, expired or already-claimed link answers `{valid: false}`, because
 *  "this link is used up" is a thing to render, not an error to catch. */
export interface AccountInvitePreview {
  valid: boolean;
  reason?: string;
  email?: string | null;
  communities?: string[] | null;
  /** True when this address has no account yet — the page says "creates your account"
   *  rather than "adds these to your account". */
  new_account?: boolean | null;
}

export async function fetchAccountInvitePreview(token: string): Promise<AccountInvitePreview> {
  const res = await fetchT(
    `${API_BASE}/admin/account-invites/preview?token=${encodeURIComponent(token)}`,
  );
  if (!res.ok) throw new ApiError(res.status, await detail(res));
  return res.json() as Promise<AccountInvitePreview>;
}

/** A session, exactly as `/admin/login` returns one, plus the community names the
 *  claim granted. */
export interface AccountInviteClaim {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: AdminUser;
  granted: string[];
}

/**
 * Spend the link: grants its communities to the invited address and signs that person
 * in. NO password is sent, asked for or changed — the account is resolved server-side
 * from the invite's own email, so there is nothing else to send.
 *
 * Stores the session through `setToken`, the same helper `login()` uses, so every
 * authenticated call afterwards works with no further ceremony. A spent, revoked or
 * expired link is a 410 — surface it, don't retry.
 */
export async function claimAccountInvite(token: string): Promise<AccountInviteClaim> {
  const res = await fetchT(`${API_BASE}/admin/account-invites/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw new ApiError(res.status, await detail(res));
  const body = (await res.json()) as AccountInviteClaim;
  setToken(body.access_token);
  identifyUser(body.user);
  return body;
}

// Pipeline health moved to `/v1` and was renamed away from the `/health` collision:
// `GET /v1/pipelines` (`@api/v1/admin`). It ships `failed_count`, `lost_access_count`
// and the canonical stage registry precomputed — three of the derived metrics doc 02
// §5.1 measured the Health page recomputing in Svelte on every render.


// The route shapes moved to `@api/v1/shapes`: they describe what a v1 endpoint
// RETURNS, so they belong beside the clients that build them rather than in this app's
// own module. Six were duplicates the package already had, and one of those —
// `ModerationItem` — had already drifted, missing the three Skool addressing ids.
// Re-exported here because the pages import them from this module.
export type {
  AiAsker,
  AiAskerBase,
  AiConversationRow,
  AiFeedbackComment,
  AiInsights,
  AiRecentQuestion,
  AiSource,
  ClockCell,
  CohortPoint,
  CohortRow,
  CommentBrief,
  CommentItem,
  CommentReason,
  LabelStat,
  MemberAnalytics,
  MemberMembership,
  ModerationItem,
  ModerationStats,
  OverwhelmWeek,
  PostAuthor,
  PostBrief,
  PostDetail,
  ReplyPartner,
  TopContent,
} from '@api/v1/shapes';

// `localDayStartISO` moved to `@api/v1/me` — it builds a v1 `since` bound, which is
// route vocabulary. Re-exported for the pages that call it.
export { localDayStartISO } from '@api';